import { useEffect, useState } from "react";
import { Plus, Ship, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Card, Empty, BigButton, Field } from "@/components/admin/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { name: "", operator: "", contact: "", capacity: 10, notes: "" };

export default function Boats() {
  const [boats, setBoats] = useState(null);
  const [form, setForm] = useState(null);
  const load = () => api.get("/admin/boats").then((r) => setBoats(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const body = { ...form, capacity: Number(form.capacity) || 0 };
    try {
      if (form.id) await api.put(`/admin/boats/${form.id}`, body);
      else await api.post("/admin/boats", body);
      toast.success("Boat saved");
      setForm(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/admin/boats/${form.id}`);
      toast.success("Boat removed");
      setForm(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="max-w-5xl" data-testid="boats-page">
      <PageHeader title="Boats & vendors" subtitle="Capacity is used for the availability calendar."
        actions={<BigButton onClick={() => setForm(EMPTY)} data-testid="add-boat-btn"><Plus className="w-5 h-5" />Add boat</BigButton>} />
      {boats && !boats.length && <Empty text="No boats yet. Add the boats you work with." testid="boats-empty" />}
      <div className="grid sm:grid-cols-2 gap-3">
        {boats?.map((b) => (
          <button key={b.id} onClick={() => setForm(b)} className="text-left" data-testid={`boat-card-${b.id}`}>
            <Card className="p-4 flex gap-3 hover:border-ocean transition-colors duration-150">
              <span className="w-12 h-12 rounded-xl bg-soft text-ocean grid place-items-center shrink-0"><Ship /></span>
              <div className="min-w-0">
                <p className="font-extrabold">{b.name}</p>
                <p className="text-sm text-slate-500">{b.capacity} guests · {b.operator || "—"} · {b.contact || "—"}</p>
                {b.notes && <p className="text-sm mt-1 line-clamp-2">{b.notes}</p>}
              </div>
            </Card>
          </button>
        ))}
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="font-admin" data-testid="boat-form-dialog">
          <DialogHeader><DialogTitle>{form?.id ? "Edit boat" : "Add boat"}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              <Field label="Boat name *"><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12" data-testid="boat-name-input" /></Field>
              <Field label="Operator / company"><Input value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })} className="h-12" data-testid="boat-operator-input" /></Field>
              <Field label="Contact (phone / WhatsApp)"><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="h-12" data-testid="boat-contact-input" /></Field>
              <Field label="Guest capacity"><Input type="number" min="0" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="h-12" data-testid="boat-capacity-input" /></Field>
              <Field label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="boat-notes-input" /></Field>
              <div className="flex gap-2">
                {form.id && <button type="button" onClick={remove} className="h-12 px-4 rounded-xl border border-border text-rose-600" data-testid="boat-delete-btn" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>}
                <BigButton type="submit" className="flex-1" data-testid="boat-save-btn">Save boat</BigButton>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
